import React, { useEffect, type JSX } from 'react';
import {
    Typography,
    Box,
    Stack,
    // Chip,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Skeleton,
    Paper,
    Switch,
    Chip,
} from '@mui/material';
import { Add, AddBox, Bolt, Delete, Search } from '@mui/icons-material';
import { BackdropLoader, CustomModal, CustomTable, CustomTransComponent, GroupPath } from 'components';
import { useTranslation } from 'react-i18next';
import Fade from '@mui/material/Fade';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ModalActions } from 'components/CustomModal';
import { EmptyState, InfoListItem } from '@brightlayer-ui/react-components';
import {
    useGetloadGroupsByIdQuery,
    useSetLoadsToLoadGroupMutation,
} from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setIsLoadRefresh, setMessageContent, setRedundancyCount } from '@fiji/common/src/features/common/commonSlice';
import { InputWithCustomPlaceholder } from '@fiji/web/src/components/InputWithCustomPlaceholder';
import { useDebounce, useLocalLoadFilter } from '@fiji/web/src/hooks';
import { AddLoads, type SelectedLoad } from './AddLoads';
import { useTypedSelector } from '@fiji/common';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { setLoadModal } from '@fiji/common/src/features/Loads/loadSlice';
import { useNewCustomTranslations } from 'hooks';
import { setLoadGroupsPayload } from '@fiji/common/src/features/loadGroups/loadGroupsSlice';
import { toFirstLetterUpperCase } from 'utils/helpers';

export const LoadLists = (): JSX.Element => {
    const { deviceId, groupId, loadGroupId } = useParams();
    const isLoadRefresh = useTypedSelector((state) => state.common.isLoadRefresh);
    const getLoadId = (load: any): string => load?.id || load?.loadId || '';
    const normalizeSelectedLoad = (load: any): SelectedLoad | null => {
        const id = load?.id || load?.loadId;

        if (!id) return null;

        return {
            ...load,
            id,
            loadName: load?.loadName ?? load?.name ?? '',
            deviceId: load?.deviceId,
        };
    };
    const [isCreateLoadModalOpen, setIsCreateLoadModalOpen] = React.useState<boolean>(false);
    const [isRemoveLoadModalOpen, setIsRemoveLoadModalOpen] = React.useState<boolean>(false);
    const [isAddLoadModalOpen, setIsAddLoadModalOpen] = React.useState<boolean>(false);
    const [selectedTableLoadIds, setSelectedTableLoadIds] = React.useState<string[]>([]);
    const { payload: currentPayload } = useTypedSelector((state: any) => state.loadGroups);

    const [payload, setPayload] = React.useState<any>({
        page: 0,
        size: 10,
        filters: {},
        ...(deviceId && { deviceId: [deviceId] }),
        ...(groupId && { groupId: [groupId] }),
    });
    const dispatch = useDispatch();
    const {
        data: loadLists,
        isLoading,
        isFetching,
        refetch,
    }: any = useGetloadGroupsByIdQuery(
        { loadGroupId: loadGroupId, body: payload },
        {
            skip: !loadGroupId,
            refetchOnMountOrArgChange: true,
        }
    );
    useNewCustomTranslations('COMMON');
    const [searchKey, setSearchKey] = React.useState<any>();
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    // const [chips, setChips] = React.useState(['Name', 'State', 'Count']);
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const tableRef = React.useRef<any>(null);
    const [selectedRemoveLoadIds, setSelectedRemoveLoadIds] = React.useState<string[]>([]);
    const [removeModalLoadIds, setRemoveModalLoadIds] = React.useState<string[]>([]);
    const [selectedAddLoads, setSelectedAddLoads] = React.useState<SelectedLoad[]>([]);
    const [setLoadsToLoadGroup, { isLoading: isSavingLoads }] = useSetLoadsToLoadGroupMutation();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const loadNumberStyle = {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#727E84',
        color: '#fff',
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
    const currentOrg = useTypedSelector(selectedOrg);
    useEffect(() => {
        const totalConfiguredLoads = loadLists?.data?.loadConfig?.length ?? 0;

        if (totalConfiguredLoads > 2) {
            dispatch(setRedundancyCount(totalConfiguredLoads));
        }
    }, [dispatch, loadLists?.data?.loadConfig?.length]);

    const allCurrentLoads = React.useMemo(() => loadLists?.data?.loadConfig ?? [], [loadLists?.data?.loadConfig]);
    const allCurrentLoadIds = (allCurrentLoads || []).map((item: any) => {
        if (typeof item === 'string') return item;

        if (item?.loadConfigId) return item.loadConfigId;

        return item?.id;
    });

    React.useEffect(() => {
        setPayload((prev: any) => ({
            ...prev,
            page: 0,
            globalSearch: debouncedValue,
        }));
    }, [debouncedValue]);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const getRedundancyState = (data: any, handleSwitch: any): JSX.Element => (
        <Switch
            onClick={(): void => handleSwitch(data)}
            checked={data?.isLoadEnabled}
            disabled={!data?.isLoadEnabledInMap || !allCurrentLoads?.length}
            color="primary"
        />
    );

    // const handleDelete = (chipToDelete: string): void => {
    //     setChips((prevChips) => prevChips.filter((chip) => chip !== chipToDelete));
    // };

    const toggleRemoveLoad = (id: string): void => {
        if (!id) return;

        setSelectedRemoveLoadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const toggleMultiAddLoad = (_loadId: string, item?: SelectedLoad): void => {
        const normalizedLoad = normalizeSelectedLoad(item);

        if (!normalizedLoad) return;

        setSelectedAddLoads((prev) => {
            const exists = prev.some((load) => getLoadId(load) === getLoadId(normalizedLoad));

            return exists
                ? prev.filter((load) => getLoadId(load) !== getLoadId(normalizedLoad))
                : [...prev, normalizedLoad];
        });
    };

    React.useEffect(() => {
        if (isLoadRefresh) {
            refetch();
            dispatch(setIsLoadRefresh(false));
        }
    }, [isLoadRefresh, refetch, dispatch]);

    const handleRemoveLoads = async (): Promise<any> => {
        if (!loadGroupId) {
            const finalLoadIds = currentPayload?.loadIds?.filter(
                (load: any) => !selectedRemoveLoadIds.some((removeId) => load?.id === removeId)
            );
            dispatch(setLoadGroupsPayload({ loadIds: finalLoadIds }));
            setSelectedRemoveLoadIds([]);
            setRemoveModalLoadIds([]);
            setSelectedTableLoadIds([]);
            setIsRemoveLoadModalOpen(false);
            tableRef?.current?.resetCheckboxes();
            return;
        }

        const finalLoadIds = allCurrentLoadIds.filter(
            (id: string) => !selectedRemoveLoadIds.some((removeId) => id.includes(removeId))
        );

        try {
            await setLoadsToLoadGroup({
                loadGroupId,
                loadIds: finalLoadIds,
                headers: {
                    'x-realm': currentRealmName,
                    organizationId: currentOrg?.id,
                },
            }).unwrap();

            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:LOADS_GROUPS.LOADS_REMOVED_SUCCESSFULLY'),
                    duration: 3000,
                })
            );
            setSelectedRemoveLoadIds([]);
            setRemoveModalLoadIds([]);
            setSelectedTableLoadIds([]);
            setIsRemoveLoadModalOpen(false);
            tableRef?.current?.resetCheckboxes();
        } catch (error: any) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: error?.data?.errorMessage?.[0] ?? t('COMMON:ERROR_MSG'),
                    duration: 3000,
                })
            );
        }
    };

    const getIsDisabled = (): boolean =>
        selectedAddLoads?.length === allCurrentLoadIds?.length ||
        selectedAddLoads?.every((item: any) => allCurrentLoadIds?.includes?.(item?.id));

    const handleAddLoads = async (): Promise<void> => {
        if (!loadGroupId) {
            dispatch(setLoadGroupsPayload({ loadIds: selectedAddLoads }));
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: `${selectedAddLoads.length} load(s) have been added successfully.`,
                    duration: 3000,
                })
            );
            setIsAddLoadModalOpen(false);
            setSelectedAddLoads([]);
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: `${selectedAddLoads.length} Load(s) have been added successfully.`,
                    duration: 3000,
                })
            );
            return;
        }
        const addedIds = selectedAddLoads.map((load) => load?.id);
        if (addedIds?.length === 0) {
            return;
        }

        try {
            await setLoadsToLoadGroup({
                loadGroupId,
                loadIds: addedIds,
                headers: {
                    'x-realm': currentRealmName,
                    organizationId: currentOrg?.id,
                },
            }).unwrap();

            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: `${selectedAddLoads.length} Load(s) have been added successfully.`,
                    duration: 3000,
                })
            );
            setSelectedAddLoads([]);
            setIsAddLoadModalOpen(false);
        } catch (error: any) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: error?.data?.errorMessage?.[0] ?? t('COMMON:ERROR_MSG'),
                    duration: 3000,
                })
            );
        }
    };

    const deleteModalActions: ModalActions[] = [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                setIsCreateLoadModalOpen(false);
                setIsAddLoadModalOpen(false);
                setSelectedAddLoads([]);
            },
        },
        {
            key: 'save',
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            disabled: (loadGroupId ? getIsDisabled() : selectedAddLoads.length === 0) || isSavingLoads,
            handleClick: (): void => {
                void handleAddLoads();
            },
        },
    ];

    const getLoadList = (): any[] => (currentPayload?.loadIds?.length ? currentPayload.loadIds : allCurrentLoads);

    const totalLoads = loadGroupId
        ? loadLists?.data?.loadGroup?.totalLoads ?? allCurrentLoads.length
        : getLoadList().length;
    const RemoveModalActions: ModalActions[] = [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                setIsRemoveLoadModalOpen(false);
            },
        },
        {
            key: 'continue',
            label: <CustomTransComponent translationKey={'COMMON:CONTINUE'} />,
            variant: 'contained',
            handleClick: (): void => {
                void handleRemoveLoads();
            },
            disabled: selectedRemoveLoadIds.length === 0 || isSavingLoads || selectedRemoveLoadIds.length >= totalLoads,
        },
    ];

    const handlePagination = (currentPage: number, pageSize: number): void => {
        setPayload((prev: any) => ({ ...prev, page: currentPage, size: pageSize }));
    };

    const handleFilterChange = (filteredValues: any, sortData?: any): void => {
        setPayload((prev: any) => ({
            ...prev,
            page: 0,
            filters: filteredValues,
            ...(sortData && Object.keys(sortData)?.length ? { sort: sortData } : {}),
        }));
    };

    const getLoadName = (item: any): any => (
        <Stack flexDirection={'row'} alignItems={'center'}>
            <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
            <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                {item?.loadName ?? item?.name}
            </Typography>
        </Stack>
    );
    const getLoadDescription = (item: any): any => (
        <Typography fontSize={14} fontWeight={400} color="#424e54">
            {item?.loadDescription ?? item?.description}
        </Typography>
    );
    const getDeviceName = (item: any): any => (
        <GroupPath pathName={item?.groupPath} deviceName={item?.deviceName} width="300px" />
    );

    const getDisplayLoadNumber = (load: SelectedLoad): string => {
        if (load?.loadNumber === undefined || load?.loadNumber === null || load?.loadNumber === '') {
            return '';
        }

        const parsedLoadNumber = Number(load.loadNumber);

        return Number.isNaN(parsedLoadNumber) ? String(load.loadNumber) : String(parsedLoadNumber + 1);
    };

    const handleSwitch = (data: any): void => {
        if (data?.isLoadEnabled) {
            dispatch(
                setLoadModal({
                    isOpen: true,
                    command: 'TURN_OFF',
                    details: data,
                })
            );
            return;
        }
        dispatch(
            setLoadModal({
                isOpen: true,
                command: 'TURN_ON',
                details: data,
            })
        );
    };

    const columns: any[] = [
        {
            header: '',
            isSelectable: true,
            width: '5%',
        },
        {
            header: t('LOADS:LOAD_NAME'),
            width: '20%',
            accessor: 'loadName',
            isFilterable: true,
            isSortable: true,
            cell: getLoadName,
            skeleton: (
                <>
                    <Skeleton animation="wave" variant="text"></Skeleton>
                </>
            ),
        },
        {
            header: t('COMMON:DEVICE'),
            width: '35%',
            accessor: 'deviceName',
            isFilterable: true,
            cell: getDeviceName,
            isSortable: true,
            skeleton: (
                <>
                    <Skeleton animation="wave" variant="text"></Skeleton>
                </>
            ),
        },
        {
            header: t('COMMON:DESCRIPTION'),
            width: '35%',
            accessor: 'loadDescription',
            isFilterable: true,
            cell: getLoadDescription,
            isSortable: true,
            skeleton: (
                <>
                    <Skeleton animation="wave" variant="text"></Skeleton>
                </>
            ),
        },
        {
            header: t('COMMON:STATE'),
            width: '5%',
            accessor: 'state',
            cell: (data: any): JSX.Element => getRedundancyState(data, handleSwitch),
            isSortable: false,
            skeleton: (
                <>
                    <Skeleton animation="wave" variant="text"></Skeleton>
                </>
            ),
        },
    ];

    const localFilteredResult = useLocalLoadFilter(currentPayload?.loadIds ?? [], payload);
    const overallLoadsCount = loadGroupId
        ? loadLists?.data?.loadGroup?.totalLoads ?? 0
        : currentPayload?.loadIds?.length ?? 0;
    const shouldShowEmptyState = overallLoadsCount === 0;

    const isAllSelected = selectedRemoveLoadIds.length > 0 && selectedRemoveLoadIds.length === totalLoads;

    const handleChipDelete = (key: any): void => {
        tableRef?.current?.resetFilters(key === 'groupId' ? 'groupPath' : key, true);
        const newPayload = JSON.parse(JSON.stringify(payload));
        if (newPayload.filters?.[key]) {
            delete newPayload.filters[key];
        }
        setPayload(newPayload);
    };

    const getChipName = (key: string): any => {
        switch (key) {
            case 'loadName':
                return t('COMMON:NAME');
            case 'isLoadEnabled':
                return t('COMMON:STATE');
            case 'deviceName':
                return t('COMMON:DEVICE');
            case 'loadDescription':
                return t('COMMON:DESCRIPTION');
            default:
                return toFirstLetterUpperCase(key);
        }
    };

    React.useEffect(() => (): any => dispatch(setLoadGroupsPayload({ loadIds: [] })), []);

    return isLoading ? (
        <BackdropLoader isOpen={true} />
    ) : (
        <>
            <Box m={3}>
                <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={10} m={2} justifyContent={'space-between'} alignItems={'center'}>
                        <InputWithCustomPlaceholder
                            id="searchKey"
                            value={searchKey ?? ''}
                            disabled={loadGroupId ? !allCurrentLoads?.length : !currentPayload?.loadIds?.length}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                setSearchKey(e.target.value);
                            }}
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            size="small"
                            InputProps={{
                                startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                            }}
                        />
                    </Stack>

                    <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                        {payload?.filters && (
                            <Stack direction="row" spacing={1}>
                                {Object.keys(payload?.filters).map(
                                    (key: string) =>
                                        key !== 'deviceId' && (
                                            <Chip
                                                key={`unique${key}`}
                                                label={getChipName(key)}
                                                onDelete={(): void => {
                                                    handleChipDelete(key);
                                                }}
                                            />
                                        )
                                )}
                            </Stack>
                        )}
                        <Box>
                            <Button
                                id="fade-button"
                                aria-controls={open ? 'fade-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                            >
                                <MoreVertIcon sx={{ color: '#727E84' }} />
                            </Button>
                            <Menu
                                id="fade-menu"
                                slotProps={{
                                    list: {
                                        'aria-labelledby': 'fade-button',
                                    },
                                }}
                                slots={{ transition: Fade }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={(): void => setAnchorEl(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setIsAddLoadModalOpen(true);
                                        setSelectedAddLoads(getLoadList() ?? []);
                                        setAnchorEl(null);
                                    }}
                                >
                                    <ListItemIcon>
                                        <AddBox sx={{ color: '#727E84' }} fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText sx={{ fontSize: 16, fontWeight: 400, color: '#424e54' }}>
                                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.ADD_LOADS'} />
                                    </ListItemText>
                                </MenuItem>
                                <MenuItem
                                    disabled={selectedTableLoadIds.length === 0}
                                    onClick={() => {
                                        setSelectedRemoveLoadIds(selectedTableLoadIds);
                                        setRemoveModalLoadIds(selectedTableLoadIds);
                                        setAnchorEl(null);
                                        setIsRemoveLoadModalOpen(true);
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{ color: selectedTableLoadIds.length === 0 ? '#b0b0b0' : '#727E84' }}
                                    >
                                        <Delete sx={{ color: '#727E84' }} fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText sx={{ fontSize: 16, fontWeight: 400, color: '#424e54' }}>
                                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.REMOVE_LOADS'} />
                                    </ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Stack>
                </Stack>

                <Box mt={2}>
                    {!shouldShowEmptyState ? (
                        <Paper elevation={1}>
                            <CustomTable
                                ref={tableRef}
                                isPagination={true}
                                keyToTraverse={'id'}
                                headers={columns}
                                handlePageChange={handlePagination}
                                handleFilterChange={handleFilterChange}
                                controlledPageSize={payload?.page}
                                data={loadGroupId ? allCurrentLoads : localFilteredResult.data}
                                isLoading={isLoading || isFetching}
                                total={
                                    loadGroupId
                                        ? loadLists?.data?.loadGroup?.totalLoads ?? 0
                                        : localFilteredResult.total
                                }
                                isWhiteRows={true}
                                handleCheckboxSelect={(ids: string[]) => {
                                    setSelectedTableLoadIds(ids);
                                }}
                                noDataFoundIcon={<Bolt fontSize={'inherit'} sx={{ color: '#727e84' }} />}
                                noDataFoundTitle={'No Loads'}
                                noDataFoundDescription={t('WIDGETS:LOADS_GROUPS.ADDED_LOADS_VIEWABLE')}
                            />
                        </Paper>
                    ) : (
                        <EmptyState
                            icon={<Bolt fontSize={'inherit'} sx={{ color: '#727e84' }} />}
                            title={(<Typography variant="h6">{'No Loads'}</Typography>) as any}
                            description={
                                <Typography variant="subtitle2">
                                    <CustomTransComponent
                                        translationKey={'WIDGETS:LOADS_GROUPS.ADDED_LOADS_VIEWABLE'}
                                    />
                                </Typography>
                            }
                            actions={
                                <Button
                                    onClick={() => setIsAddLoadModalOpen(true)}
                                    variant={'contained'}
                                    color={'primary'}
                                    startIcon={<Add />}
                                >
                                    <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.ADD_LOADS'} />
                                </Button>
                            }
                        />
                    )}
                </Box>
            </Box>

            <CustomModal
                isOpen={isCreateLoadModalOpen}
                isLoading={false}
                type="primary"
                actions={deleteModalActions}
                onClose={() => setIsCreateLoadModalOpen(false)}
                customHeader={
                    <>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'} />
                    </>
                }
                actionsDivider={true}
            ></CustomModal>

            {/* Remove Loads */}
            <CustomModal
                isOpen={isRemoveLoadModalOpen}
                onClose={() => {
                    setIsRemoveLoadModalOpen(false);
                    setSelectedRemoveLoadIds([]);
                    setRemoveModalLoadIds([]);
                }}
                isLoading={isSavingLoads}
                type="primary"
                actions={RemoveModalActions}
                header={<CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.REMOVE_LOADS'} />}
                subHeader={
                    <>
                        {!isAllSelected ? (
                            <CustomTransComponent
                                translationKey={'WIDGETS:LOADS_GROUPS.REMOVE_SELECTED_LOADS'}
                                replace={{ loadCount: selectedRemoveLoadIds?.length }}
                            />
                        ) : (
                            <CustomTransComponent
                                translationKey={'WIDGETS:LOADS_GROUPS.REMOVE_ALL_SELECT_LOADS_DESCRIPTION'}
                            />
                        )}
                    </>
                }
                actionsDivider={true}
            >
                <Box maxHeight={250} overflow="auto">
                    {getLoadList()
                        ?.filter((item: any) => removeModalLoadIds.includes(getLoadId(item)))
                        .map((item: any) => (
                            <InfoListItem
                                key={getLoadId(item)}
                                title={''}
                                subtitle={[
                                    <>
                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
                                            <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                                                {item?.loadName ?? item?.name}
                                            </Typography>
                                        </Stack>
                                    </>,
                                ]}
                                info={[
                                    <GroupPath
                                        key={item?.id}
                                        pathName={item?.groupPath}
                                        deviceName={item?.deviceName}
                                        width="250px"
                                    />,
                                ]}
                                icon={
                                    <Checkbox
                                        checked={selectedRemoveLoadIds.includes(getLoadId(item))}
                                        onChange={() => toggleRemoveLoad(getLoadId(item))}
                                    />
                                }
                                hidePadding
                                divider="full"
                            />
                        ))}
                </Box>
            </CustomModal>

            {/* Add Loads */}
            <CustomModal
                isOpen={isAddLoadModalOpen}
                onClose={() => {
                    setIsAddLoadModalOpen(false);
                    setSelectedAddLoads([]);
                }}
                isLoading={isSavingLoads}
                type="secondary"
                actions={deleteModalActions}
                header={<CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.ADD_LOADS'} />}
                hidePadding
                subHeader={
                    <CustomTransComponent
                        translationKey={'WIDGETS:LOADS_GROUPS.SELECT_LOADS_TO_ADD_TO_THE_LOAD_GROUP'}
                    />
                }
                actionsDivider={true}
            >
                <AddLoads
                    loadGroupId={loadGroupId}
                    selectedLoadIds={selectedAddLoads}
                    onToggleMultiLoad={toggleMultiAddLoad}
                    setSelectedAddLoads={setSelectedAddLoads}
                    existingLoadIds={allCurrentLoadIds}
                />
            </CustomModal>
        </>
    );
};
